/**
 * Shared Mobile Detection Composable
 *
 * Platform-agnostic mobile detection with injectable dependencies.
 * The Quasar $q.screen object must be injected by the consuming app.
 *
 * @example
 * // In ui/ or si/ app:
 * import { useMobile } from '@shared/composables';
 * import { useQuasar } from 'quasar';
 *
 * const $q = useQuasar();
 * const { isMobile, isTablet, isDesktop } = useMobile({
 *   screen: $q.screen,
 *   appEnv: import.meta.env.VITE_APP_ENV
 * });
 */

import { computed, onMounted, onUnmounted, ref, type ComputedRef } from 'vue';

/**
 * Quasar-like screen interface (subset of what we need)
 */
export interface ScreenInterface {
  lt: { md: boolean };
  md: boolean;
  gt: { md: boolean };
}

/**
 * Options for useMobile composable
 */
export interface UseMobileOptions {
  /** Quasar screen object for responsive detection */
  screen?: ScreenInterface;
  /** App environment (android, ios, web) */
  appEnv?: string;
}

/**
 * Return type of useMobile composable
 */
export interface UseMobileReturn {
  isMobile: ComputedRef<boolean>;
  isNativeApp: ComputedRef<boolean>;
  isAndroid: ComputedRef<boolean>;
  isIOS: ComputedRef<boolean>;
  isWeb: ComputedRef<boolean>;
  isTablet: ComputedRef<boolean>;
  isDesktop: ComputedRef<boolean>;
  isLargeScreen: ComputedRef<boolean>;
  screenWidth: ReturnType<typeof ref<number>>;
  setSafeAreaInsets: () => void;
}

/**
 * Mobile detection composable with injectable dependencies
 *
 * @param options - Configuration options
 * @returns Mobile detection computed properties and utilities
 */
export function useMobile(options: UseMobileOptions = {}): UseMobileReturn {
  const { screen, appEnv = '' } = options;

  // Reactive screen width tracking
  const screenWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024);

  // Update screen width on resize
  const updateScreenWidth = () => {
    screenWidth.value = window.innerWidth;
  };

  // Native mobile app detection
  // Covers bare "ios"/"android" and tiered variants like "ios.local", "ios.staging",
  // "ios.production", "android.local", etc. — matches the convention in boot/device.ts.
  const isNativeApp = computed(() => {
    return (
      appEnv === 'android' ||
      appEnv === 'ios' ||
      appEnv.startsWith('android.') ||
      appEnv.startsWith('ios.')
    );
  });

  // Responsive mobile detection (small screen OR native app)
  const isMobile = computed(() => {
    // Native app is always mobile
    if (isNativeApp.value) return true;
    // Use Quasar screen if available, otherwise use width
    if (screen) {
      return screen.lt.md || screenWidth.value < 768;
    }
    return screenWidth.value < 768;
  });

  const isAndroid = computed(() => {
    return appEnv === 'android' || appEnv.startsWith('android.');
  });

  const isIOS = computed(() => {
    return appEnv === 'ios' || appEnv.startsWith('ios.');
  });

  const isWeb = computed(() => {
    return !isNativeApp.value;
  });

  // Is tablet (medium screens)
  const isTablet = computed(() => {
    if (screen) {
      return screen.md || (screenWidth.value >= 768 && screenWidth.value < 1024);
    }
    return screenWidth.value >= 768 && screenWidth.value < 1024;
  });

  // Is desktop (large screens)
  const isDesktop = computed(() => {
    if (screen) {
      return screen.gt.md || screenWidth.value >= 1024;
    }
    return screenWidth.value >= 1024;
  });

  // Is large/HD screen (1366px+)
  const isLargeScreen = computed(() => {
    return screenWidth.value >= 1366;
  });

  const setSafeAreaInsets = () => {
    if (typeof document === 'undefined') return;

    // Add platform class to body - CSS handles safe area values
    if (isAndroid.value) {
      document.body.classList.add('platform-android');
    } else if (isIOS.value) {
      document.body.classList.add('platform-ios');
    } else {
      document.body.classList.add('platform-web');
    }
  };

  // Auto-apply safe area insets and set up resize listener
  onMounted(() => {
    setSafeAreaInsets();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', updateScreenWidth);
    }
  });

  onUnmounted(() => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', updateScreenWidth);
    }
  });

  return {
    isMobile,
    isNativeApp,
    isAndroid,
    isIOS,
    isWeb,
    isTablet,
    isDesktop,
    isLargeScreen,
    screenWidth,
    setSafeAreaInsets,
  };
}
